import type { PortalDog } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { G } from './glyphs';
import { Tag } from './Tag';
import { cardTag } from './registrationRules';
import { BookBar, BookPage, RangeNotes } from './RequestChrome';
import { PendingButton } from '../components/common/PendingButton';
import { DOGS_DETAILS_NOTE, type DogStayAnswer } from './requestRules';

// `c5` — step 1, who is coming (flow 2.1, R-REQ-2).
//
// EVERY DOG ON THE HOUSEHOLD, NO TICK BOX (Freddi, 2026-10-08: a request is
// for the whole household, as a desk booking is; deviation 1). What is asked
// is how each one is travelling THIS time — medication, and keep apart — and
// those answers go on the stay, never on the dog (AC2). A later request asks
// again, so each starts from No.

export const MEDS_MAX = 500;

const initialOf = (name: string) => (name.trim()[0] ?? '?').toUpperCase();

function Seg({ label, on, onPick, name }: { label: string; on: boolean; onPick: (v: boolean) => void; name: string }) {
  return (
    <div className="cp-seg sm" role="radiogroup" aria-label={`${label} — ${name}`}>
      <button type="button" role="radio" aria-checked={!on} className={on ? '' : 'on'} onClick={() => onPick(false)}>
        No
      </button>
      <button type="button" role="radio" aria-checked={on} className={on ? 'on' : ''} onClick={() => onPick(true)}>
        Yes
      </button>
    </div>
  );
}

export interface RequestDogsStepProps {
  dogs: PortalDog[];
  answers: Record<string, DogStayAnswer>;
  note: string;
  today: string;
  /** Why the step will not move on, in words — a Yes with no instructions. */
  error: string | null;
  onChange: (dogId: string, answer: DogStayAnswer) => void;
  onNote: (note: string) => void;
  onNext: () => void;
  onBack: () => void;
  /** The bar's close — the wizard decides whether to ask first. */
  onClose: () => void;
  /** Waiting on the calendar step 2 opens on. */
  opening?: boolean;
}

export function RequestDogsStep({ dogs, answers, note, today, error, onChange, onNote, onNext, onBack, onClose, opening = false }: RequestDogsStepProps) {
  return (
    <BookPage onSubmit={onNext} onClose={onClose}>
      <BookBar step={1} onBack={onBack} />
      <h1 className="cp-h1">Who’s coming?</h1>
      <p className="cp-p mute cp-lead">Tell us how they are travelling this time — it can change from one stay to the next.</p>
      <p className="cp-eyebrow cs-eyebrow">Your dogs</p>

      <div className="cp-list">
        {dogs.map((dog) => {
          const a = answers[dog.id] ?? { meds: 'none', instructions: '', keepApart: false };
          const set = (patch: Partial<DogStayAnswer>) => onChange(dog.id, { ...a, ...patch });
          const card = cardTag(dog, today);
          return (
            <div key={dog.id} className="cp-choice col">
              <div className="cp-choice-h">
                <Avatar name={dog.name} id={dog.id} size={38} kind="dog" initials={initialOf(dog.name)} />
                <span className="txt">
                  <b>{dog.name}</b>
                </span>
                <Tag tone={card.tone} glyph={G.syringe({ size: 12 })}>
                  {card.text}
                </Tag>
              </div>
              <div className="dogq">
                <div className="dogq-r">
                  {/* Questions, not labels (Freddi, 2026-10-08): short, and answered by the switch beside them. */}
                  <span className="dogq-l">{G.pill({ size: 13 })}On medication?</span>
                  <Seg label="On medication?" name={dog.name} on={a.meds === 'yes'} onPick={(v) => set({ meds: v ? 'yes' : 'none' })} />
                </div>
                {a.meds === 'yes' && (
                  <input
                    className={'cp-input sm' + (error && !a.instructions.trim() ? ' is-bad' : '')}
                    name={`instructions-${dog.id}`}
                    aria-label={`${dog.name}'s medication`}
                    value={a.instructions}
                    maxLength={MEDS_MAX}
                    placeholder="What, how much, and when"
                    onChange={(e) => set({ instructions: e.target.value })}
                  />
                )}
                <div className="dogq-r">
                  <span className="dogq-l">{G.flag({ size: 13 })}Keep apart from others?</span>
                  <Seg label="Keep apart from others?" name={dog.name} on={a.keepApart} onPick={(v) => set({ keepApart: v })} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cp-field grow">
        <label className="ico" htmlFor="request-note">
          {G.bubble({ size: 13 })}Anything else Patricia should know?
        </label>
        <textarea
          id="request-note"
          className="cp-input area"
          name="note"
          rows={3}
          maxLength={1000}
          value={note}
          placeholder="Optional — feeding, habits, a vet note…"
          onChange={(e) => onNote(e.target.value)}
        />
      </div>

      {error && (
        <p className="cp-fail" role="alert">
          {error}
        </p>
      )}
      <div className="cp-actions">
        {/* The dogs are Patricia's to change — said here, pinned with the button that confirms them. */}
        <RangeNotes notes={[DOGS_DETAILS_NOTE]} />
        {/* Busy only if the calendar has not landed by the time they tap — it is
            read while they answer this step, so step 2 opens fully drawn. */}
        <PendingButton type="submit" className="cp-btn primary" icon={G.calendar({ size: 15 })} busy={opening}>
          Submit your dogs
        </PendingButton>
      </div>
    </BookPage>
  );
}
